/**
 * Tag Selector Module
 * Handles tag button selection and image visibility
 */
class TagSelector {
  /**
   * Initialize the TagSelector
   * @param {string} buttonSelector - CSS selector for tag buttons
   */
  constructor(buttonSelector = '.c-tag__select--btn') {
    this.buttonSelector = buttonSelector;
    this.buttons = [];
    this.init();
  }

  /**
   * Initialize the tag selector
   */
  init() {
    this.bindEvents();
  }

  /**
   * Bind event listeners to tag buttons
   */
  bindEvents() {
    this.buttons = document.querySelectorAll(this.buttonSelector);

    this.buttons.forEach(button => {
      button.addEventListener('click', e => this.handleClick(e));
    });
  }

  /**
   * Handle button click event
   * @param {Event} event - Click event
   */
  handleClick(event) {
    const button = event.currentTarget;
    this.toggleSelection(button);
  }

  /**
   * Toggle selection state of a button
   * @param {HTMLElement} button - Button element to toggle
   */
  toggleSelection(button) {
    const isSelected = button.classList.contains('selected');

    if (isSelected) {
      this.deselectButton(button);
    } else {
      this.selectButton(button);
    }
  }

  /**
   * Select a button (add selected class, hide image)
   * @param {HTMLElement} button - Button element to select
   */
  selectButton(button) {
    button.classList.add('selected');
    this.hideImage(button);
    button.setAttribute('aria-pressed', 'true');
  }

  /**
   * Deselect a button (remove selected class, show image)
   * @param {HTMLElement} button - Button element to deselect
   */
  deselectButton(button) {
    button.classList.remove('selected');
    this.showImage(button);
    button.setAttribute('aria-pressed', 'false');
  }

  /**
   * Hide the image inside a button
   * @param {HTMLElement} button - Button element containing the image
   */
  hideImage(button) {
    const image = button.querySelector('img');
    if (image) {
      image.style.display = 'none';
    }
  }

  /**
   * Show the image inside a button
   * @param {HTMLElement} button - Button element containing the image
   */
  showImage(button) {
    const image = button.querySelector('img');
    if (image) {
      image.style.display = '';
    }
  }

  /**
   * Destroy the tag selector instance
   */
  destroy() {
    this.buttons.forEach(button => {
      button.removeEventListener('click', this.handleClick);
    });
    this.buttons = [];
  }
}

/**
 * Initialize TagSelector when DOM is ready
 */
function initTagSelector() {
  new TagSelector();
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTagSelector);
} else {
  initTagSelector();
}

// Export for use in other modules if needed
export default TagSelector;
